import Controller, { OnBeforeSubmit, OnBlur, OnCancel, OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { FragmentNames, IntakesAndOutputsEditorAliases } from '../../../codegen';
import BaseController from '../../BaseController';
import { Inject } from '@odinjs/odin';
import { ActionAPI, ButtonAPI, DynamicFieldsAPI, FetchAPI, FieldAPI, FieldsetAPI, FormAPI, InputAPI, Metadata, ToastService } from '@philips-internal/oswald-ui-components';
import moment from 'moment';
import { AuthenticationService, HttpResponse, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import { Value } from '@philips-internal/oswald-ui-core';
import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isEmptyObject } from '@philips-internal/oswald-ui-utils';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import ShowToast from '../../../utils/toast-service';
import { SimpleDialog } from '../../dialog-helper';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';

interface IntakesOutputsProps {
  gainLoss: string;
  requireDevice: string;
  requireTopography: string;
  sequenceNumber: number;
  showDevice: string;
  showSide: string;
  volumeIncidents: string;
}

interface FormValues {
  description: string;
  device?: DeviceProps;
  occurrences?: number;
  recordDate: string
  recordTime: RecordTimeProps;
  retroactiveJustification: string;
  side?: SideProps;
  topography?: TopographyProps;
  type: TypeProps
  volume?: number;
  weight?: number;
}

interface DeviceProps {
  deviceDescription: string;
  deviceId?: number;
  sequenceNumber: number;
  topographyDescription?: string;
  topographySequence?: number;
}

interface RecordTimeProps {
  apapDescription: string;
  apapValue: number;
}

interface SideProps {
  sideDescription: string;
  sideSequence: string;
}

interface TypeProps {
  sequenceNumber: number;
  typeDescription: string;
}

interface TopographyProps {
  sequenceNumber: number;
  topographyDescription: string;
}

interface IntakesOutputsValuesProps {
  [key: string]: string | number;
}

interface AttributesProps {
  component: Metadata;
  values: IntakesOutputsValuesProps;
}

interface IntakesOutputsLimitResponse {
  ds_retorno_p?: string
}

interface TimeValue {
  currentDateTime: string;
}

enum visualizationType {
  VOLUME = 'V',
  WEIGHT = 'P',
  ABLE = 'S',
  REQUIRED = 'O'
}

@Controller({ fragment: FragmentNames.INTAKES_AND_OUTPUTS_EDITOR })
class IntakesAndOutputsEditorController extends BaseController {

  @Inject(IntakesAndOutputsEditorAliases.INTAKES_AND_OUTPUTS_EDITOR_FORM)
    intakesOutputsformApi!: FormAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELD_VOLUME)
    fieldVolumeApi!: FieldAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELD_WEIGHT)
    fieldWeightApi!: FieldAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELD_OCCURRENCES)
    fieldOccurrencesApi!: FieldAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELD_DEVICE)
    fieldDeviceApi!: FieldAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELD_TOPOGRAPHY)
    fieldTopographyApi!: FieldAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELD_SIDE)
    fieldSideApi!: FieldAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELD_RETROACTIVE)
    fieldRetroactiveApi!: FieldAPI;

  @Inject(IntakesAndOutputsEditorAliases.DYNAMIC_FIELDS_INTAKES_OUTPUTS)
    dynamicFieldsApi!: DynamicFieldsAPI;

  @Inject(IntakesAndOutputsEditorAliases.FIELDSET_DYNAMIC_FIELD)
    fieldSetDynamicFieldApi!: FieldsetAPI;

  @Inject(IntakesAndOutputsEditorAliases.ACTION_DELETE_INTAKES_OUTPUTS)
    deleteActionApi!: ActionAPI;

  @Inject(IntakesAndOutputsEditorAliases.RELEASE_BUTTON)
    buttonReleaseApi!: ButtonAPI;

  @Inject(IntakesAndOutputsEditorAliases.INPUT_DATETIME)
    recordDateInputApi!: InputAPI;

  validateFormFields(sequenceNumber: number, edit: boolean): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    HttpService.get(Endpoint.INTAKES_OUTPUTS_VISUALIZATION, { encounterId, patientId, sequenceNumber }).then((response) => {
      const intakesVisualization = response.body as IntakesOutputsProps;
      if(intakesVisualization.requireDevice === 'S') {
        this.validateConsistType(intakesVisualization.sequenceNumber);
      }
      this.handleIncidentsFields(intakesVisualization.volumeIncidents);
      this.handleFormFields(intakesVisualization.showDevice, this.fieldDeviceApi);
      this.handleFormFields(intakesVisualization.requireTopography, this.fieldTopographyApi);
      this.handleFormFields(intakesVisualization.showSide, this.fieldSideApi);
      this.handleDynamicFields(intakesVisualization.sequenceNumber, edit);
      this.context.set({
        type: intakesVisualization.gainLoss
      })
    })
  }

  validateConsistType(sequenceNumber: number): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    HttpService.get(Endpoint.INTAKES_OUTPUTS_VALIDATE_CONSIST, { encounterId, patientId, sequenceNumber }).then(response => {
      const procedureMessage = response.body as Record<string, Value>;
      const validType = isEmptyObject(procedureMessage);

      if(!validType) {
        ToastService.open({
          dismissible: true,
          id: 'error-invalid-type',
          level: ToastLevel.WARNING,
          message: String(procedureMessage.ds_consistencia_p),
          showIcon: true,
          timeout: 5000,
          type: ToastType.BUBBLE,
        });
      }
      this.intakesOutputsformApi.setDisabled(!validType);
      this.intakesOutputsformApi.validate();
    })
  }

  handleIncidentsFields(intakesVisualization: string): void {
    const validator: Validator = { name: 'required', options: {} };

    switch (intakesVisualization) {
      case visualizationType.VOLUME:
        this.fieldVolumeApi.setVisible(true);
        if(this.fieldVolumeApi.getValidators().length === 0) {
          this.fieldVolumeApi.addValidator(validator);
          this.fieldVolumeApi.validate();
        }
        this.fieldWeightApi.setVisible(false);
        this.fieldWeightApi.removeValidator('required');
        this.fieldOccurrencesApi.setVisible(false);
        this.fieldOccurrencesApi.removeValidator('required');
        break;
      case visualizationType.WEIGHT:
        this.fieldWeightApi.setVisible(true);
        if(this.fieldWeightApi.getValidators().length === 0) {
          this.fieldWeightApi.addValidator(validator);
          this.fieldWeightApi.validate();
        }
        this.fieldVolumeApi.setVisible(false);
        this.fieldVolumeApi.removeValidator('required');
        this.fieldOccurrencesApi.setVisible(false);
        this.fieldOccurrencesApi.removeValidator('required');
        break;
      default:
        this.fieldOccurrencesApi.setVisible(true);
        if(this.fieldOccurrencesApi.getValidators().length === 0) {
          this.fieldOccurrencesApi.addValidator(validator);
          this.fieldOccurrencesApi.validate();
        }
        this.fieldWeightApi.setVisible(false);
        this.fieldWeightApi.removeValidator('required');
        this.fieldVolumeApi.setVisible(false);
        this.fieldVolumeApi.removeValidator('required');
        break;
    }
  }

  handleFormFields(fieldType: string, fieldApi: FieldAPI): void {
    const validator: Validator = { name: 'required', options: {} };

    switch (fieldType) {
      case visualizationType.ABLE:
        fieldApi.setVisible(true);
        break;
      case visualizationType.REQUIRED:
        fieldApi.setVisible(true);
        fieldApi.addValidator(validator);
        break;
      default:
        fieldApi.setVisible(false);
        break;
    }
  }

  handleDynamicFields(typeSequence: number, edit: boolean): void {
    const { patientId, encounterId, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const sequenceId = edit ? sequenceNumber : typeSequence;
    this.fieldSetDynamicFieldApi.setVisible(false);

    HttpService.get(Endpoint.INTAKES_OUTPUTS_ADDITIONAL_INFORMATION, { encounterId, patientId, sequenceNumber: sequenceId }).then((response) => {
      const responseBody = response.body as AttributesProps;
      const dynamicFields = responseBody.component as Metadata;
      this.context.set({ dynamicFields });

      if(!edit) {
        this.fieldSetDynamicFieldApi.setVisible(true);
        return;
      }

      if(responseBody.values) {
        this.fieldSetDynamicFieldApi.setVisible(true);
        const keys = Object.keys(responseBody.values);
        const values = Object.values(responseBody.values);
        const formData = this.intakesOutputsformApi.getRecord();
        const dynamicValues = {} as IntakesOutputsValuesProps;

        keys.forEach((key, index) => {
          dynamicValues[key] = values[index];
        })

        this.intakesOutputsformApi.setRecord({ ...formData, dynamicValues });
      }
    }).catch(() => {
      this.fieldSetDynamicFieldApi.setVisible(false);
    })
  }

  confirmCancelAndRelease(patientId: string, encounterId: number, sequenceNumber: number): void {
    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'patient-evolution.release-title-evolution',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.discard() },
      onClickOk: async () => { this.release(patientId, encounterId, sequenceNumber) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  discard(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  release(patientId: string, encounterId: number, sequenceNumber: number): void {
    HttpService.put(Endpoint.INTAKES_OUTPUTS_RELEASE, { encounterId, patientId, sequenceNumber }).then(response => {
      if (response.status === 200 && response.body) {
        applicationContext.popNavigationItem();
      }
    }).catch(error => {
      this.buttonReleaseApi.setVisible(true);
      this.intakesOutputsformApi.setEditing(true);
      this.intakesOutputsformApi.setDisabled(true);
      ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
    })
  }

  delete(patientId: string, encounterId: number, sequenceNumber: number): void {
    HttpService.delete(Endpoint.INTAKES_OUTPUTS, { encounterId, patientId, sequenceNumber }).then(response => {
      if (response.status === 200 && response.body) {
        applicationContext.popNavigationItem();
      }
    })
  }

  cancelDiscard(record: Record<string, Value>): void {
    this.intakesOutputsformApi.setDisabled(false);
    this.intakesOutputsformApi.setData({
      ...record
    });
    this.intakesOutputsformApi.setEditing(true);
  }

  cancel(record: Record<string, Value>): void {
    const dialogCancelParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'clinical-notes.discard-changes',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.cancelDiscard(record) },
      onClickOk: () => { this.discard() },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit',
    }

    SimpleDialog.openDialog(dialogCancelParams);
  }

  getCurrentDate(): Promise<HttpResponse<TimeValue>> {
    return HttpService.get<TimeValue>(Endpoint.GET_CURRENT_DATE_TIME)
  }

  private getApapValue(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const dateParam = this.context.get('recordDate');
    const recordDate = moment(dateParam).utc().format(ISO_FULL_DATE_FORMAT);

    HttpService.get(Endpoint.INTAKES_OUTPUTS_APAP_VALUE, { encounterId, patientId, recordDate}, {}).then(response => {
      if(response.body) {
        const {apapDescription, apapValue } = response.body as Record<string, Value>;
        const currentRecord = this.intakesOutputsformApi.getRecord();
        this.intakesOutputsformApi.setRecord({
          ...currentRecord,
          recordTime: {
            apapDescription,
            apapValue
          },
        })
      }
    })
  }

  creationIntakesOutputs(): void {
    this.getCurrentDate()
      .then((response) => {
        if(!response.body?.currentDateTime) {
          Promise.reject();
        }
        const renderDate = moment(response.body?.currentDateTime).utc().format(ISO_FULL_DATE_FORMAT);
        this.intakesOutputsformApi.setData({ recordDate: renderDate });
        this.context.set({ recordDate: renderDate });
        this.getApapValue()
      })
      .catch(() => this.intakesOutputsformApi.setData({ recordDate: moment() }));
  }

  editIntakesOutputs(sequenceNumber: number): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const edit = true;
    HttpService.get(Endpoint.INTAKES_OUTPUTS_DETAIL, {
      encounterId,
      patientId,
      sequenceNumber
    }
    ).then(response => {
      const record = response.body as Record<string, Value>;
      this.context.set({recordDate: moment(String(record.recordDate)).utc().format(ISO_FULL_DATE_FORMAT) });

      if(record.retroactiveJustification) {
        this.fieldRetroactiveApi.setVisible(true);
      }

      const currentRecord = this.intakesOutputsformApi.getRecord();
      this.intakesOutputsformApi.setRecord({
        ...currentRecord,
        description: record.description,
        device: {
          deviceDescription: record.deviceDescription,
          deviceId: record.deviceId,
          sequenceNumber: record.deviceSequence,
        },
        occurrences: record.incidentsQuantity,
        recordDate: moment(String(record.recordDate)).utc().format(ISO_FULL_DATE_FORMAT),
        recordTime: {
          apapDescription: record.apapHourDescription,
          apapValue: record.recordTime
        },
        retroactiveJustification: record.retroactiveJustification,
        side: {
          sideDescription: record.sideDescription,
          sideSequence: record.sideSequence,
        },
        topography: {
          sequenceNumber: record.topographySequence,
          topographyDescription: record.topographyDescription,
        },
        type: {
          sequenceNumber: record.typeSequence,
          typeDescription: record.type
        },
        volume: record.volumeQuantity,
        weight: record.weightQuantity,
      });

      this.validateFormFields(Number(record.typeSequence), edit);
    });
  }

  insertIntakesOutputs(body: Record<string, Value>): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.INTAKES_OUTPUTS_CONSIST, { encounterId, patientId }, { body }).then(response => {
      if(response.body) {
        const responseBody = response.body as Record<string, Value>;
        const sequenceNumber = responseBody.nr_sequencia_p;
        if(sequenceNumber) {
          this.confirmCancelAndRelease(String(patientId), Number(encounterId), Number(sequenceNumber));
        }
      }
    }).catch(error => {
      this.intakesOutputsformApi.setEditing(true);
      ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
    });
  }

  updateIntakesOutputs(body: Record<string, Value>, event: AbortableSubmitEvent ): void {
    const { patientId, encounterId, sequenceNumber } = this.historyService.getCurrentRouteParams();
    HttpService.put(Endpoint.INTAKES_OUTPUTS, { encounterId, patientId, sequenceNumber }, { body }).then(response => {
      if(response.body) {
        const responseBody = response.body as Record<string, Value>;
        if(responseBody) {
          this.confirmCancelAndRelease(String(patientId), Number(encounterId), Number(sequenceNumber));
        }
      }
    }).catch(error => {
      event.abort();
      this.intakesOutputsformApi.setEditing(true);
      this.intakesOutputsformApi.setDisabled(false);
      ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
    });
  }

  invalidDate(): boolean {
    const recordDate = this.context.get('recordDate') as moment.Moment;
    const newDate = moment(recordDate);
    const today = moment();
    if (newDate.diff(today, 'days') > 0) {
      ToastService.open({
        dismissExpression: MessageService.translate('warning.clinical-note.later-date'),
        dismissible: false,
        highlight: false,
        id: 'administration-by-process',
        level: ToastLevel.WARNING,
        message: MessageService.translate('warning.clinical-note.later-date'),
        placement: Placement.BOTTOM,
        showIcon: true,
        timeout: 5000,
        type: ToastType.BANNER,
      });
      return true;
    }
    return false;
  }

  @OnMount({target: IntakesAndOutputsEditorAliases.INTAKES_AND_OUTPUTS_EDITOR_LAYOUT})
  onMount(): void {
    const token = AuthenticationService.getJWTToken();
    const department = token?.claims['ext_sector_id'] as number;
    const { encounterId, patientId, type, sequenceNumber } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({
      department,
      encounterId,
      patientId,
      sequenceNumber,
      type,
    });
  }

  @OnMount({ target: IntakesAndOutputsEditorAliases.RELEASE_BUTTON })
  onReleaseButtonMount(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();
    if(sequenceNumber) {
      this.intakesOutputsformApi.setDisabled(true);
      this.buttonReleaseApi.setVisible(true);
    } else {
      this.intakesOutputsformApi.setDisabled(false);
      this.buttonReleaseApi.setVisible(false);
    }

  }

  @OnClick({ target: IntakesAndOutputsEditorAliases.RELEASE_BUTTON })
  onReleaseClick(): void {
    const { encounterId, patientId, sequenceNumber } = this.historyService.getCurrentRouteParams();
    this.buttonReleaseApi.setVisible(false);
    this.intakesOutputsformApi.setEditing(false);
    this.intakesOutputsformApi.setDisabled(true);
    this.release(String(patientId), Number(encounterId), Number(sequenceNumber));
  }

  @OnMount({ target: IntakesAndOutputsEditorAliases.FETCH_TYPE })
  onFetchTypeMount(event: MountEvent): void {
    const fetchType = event.target as FetchAPI;
    fetchType.setParameters({}, { skip: true });
  }

  @OnMount({ target: IntakesAndOutputsEditorAliases.FETCH_RECORD_TIME })
  onFetchApapMount(event: MountEvent): void {
    const fetchApap = event.target as FetchAPI;
    fetchApap.setParameters({}, { skip: true });
  }

  @OnMount({target: IntakesAndOutputsEditorAliases.INTAKES_AND_OUTPUTS_EDITOR_FORM})
  onFormMount(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();
    if(sequenceNumber) {
      this.editIntakesOutputs(Number(sequenceNumber));
      return;
    }
    this.creationIntakesOutputs();
  }

  @OnMount({ target: IntakesAndOutputsEditorAliases.ACTION_DELETE_INTAKES_OUTPUTS })
  onDeleteMount(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();
    if(sequenceNumber) {
      this.deleteActionApi.setEnabled(true);
    }
  }

  @OnBlur({ target: IntakesAndOutputsEditorAliases.FIELD_RECORD_DATE })
  onBlurDate(event: ChangeEvent<FieldAPI>): void {
    if(event.target.getValue()) {
      const recordDate = moment(String(event.target.getValue()));
      const today = moment();
      if (recordDate.isBefore(today)) {
        this.fieldRetroactiveApi.setVisible(true);
        const validator: Validator = { name: 'required', options: {} };
        this.fieldRetroactiveApi.addValidator(validator);
        this.fieldRetroactiveApi.validate();
      } else {
        this.fieldRetroactiveApi.removeValidator('required');
        this.fieldRetroactiveApi.setVisible(false);
      }
    }
  }

  @OnChange({target: IntakesAndOutputsEditorAliases.FIELD_TYPE})
  onTypeChange(event: ChangeEvent<FieldAPI>): void {
    const values = event.value as unknown as TypeProps;
    if(values) {
      this.validateFormFields(values.sequenceNumber, false);
    }
  }

  @OnMount(IntakesAndOutputsEditorAliases.DYNAMIC_FIELDS_INTAKES_OUTPUTS)
  handleMountDynamicFields(): void {
    const dynamicFields = this.context.get('dynamicFields');
    if (dynamicFields) {
      this.dynamicFieldsApi.setMetadata(dynamicFields);
    }
  }

  @OnChange({target: IntakesAndOutputsEditorAliases.FIELD_DEVICE})
  onChangeDevice(event: ChangeEvent<FieldAPI>): void {
    const values = event.value as unknown as DeviceProps;
    if(values) {
      const currentRecord = this.intakesOutputsformApi.getRecord();
      this.intakesOutputsformApi.setRecord({
        ...currentRecord,
        device: {
          deviceDescription: values.deviceDescription,
          deviceId: values.deviceId,
          sequenceNumber: values.sequenceNumber,
        },
        topography: {
          sequenceNumber: values?.topographySequence,
          topographyDescription: values?.topographyDescription,
        },
      })
    }
  }

  @OnChange({ target: IntakesAndOutputsEditorAliases.INPUT_DATETIME })
  onDataTimeInputChange(evt: ChangeEvent<InputAPI>): void {
    const newDateState = moment(String(evt.value));
    this.context.set({recordDate: newDateState});
  }

  @OnClick({ target: IntakesAndOutputsEditorAliases.ACTION_DELETE_INTAKES_OUTPUTS })
  onDelete(): void {
    const { patientId, encounterId, sequenceNumber } = this.historyService.getCurrentRouteParams();

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { applicationContext.popNavigationItem(); },
      onClickOk: async () => { this.delete(String(patientId), Number(encounterId), Number(sequenceNumber)) },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'patient-evolution.delete-notnow-evolution',
      titleOnOk: 'patient-evolution.delete-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  @OnCancel({target: IntakesAndOutputsEditorAliases.INTAKES_AND_OUTPUTS_EDITOR_FORM})
  onFormBeforeCancel(): void {
    const record = this.intakesOutputsformApi.getRecord();
    this.intakesOutputsformApi.setRecord(record);
    this.intakesOutputsformApi.setDisabled(true);
    this.cancel(record);
  }

  @OnChange({ target: IntakesAndOutputsEditorAliases.INTAKES_AND_OUTPUTS_EDITOR_FORM })
  onClinicalNotesChanges(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();

    if (sequenceNumber) {
      this.intakesOutputsformApi.setDisabled(false);
    }
    this.intakesOutputsformApi.validate();
  }

  @OnBeforeSubmit({target: IntakesAndOutputsEditorAliases.INTAKES_AND_OUTPUTS_EDITOR_FORM})
  onSubmitForm(event: AbortableSubmitEvent): void {
    event.abort()
    const { sequenceNumber, patientId } = this.historyService.getCurrentRouteParams();

    if(this.invalidDate()) {
      event.abort();
      return;
    }

    const keys = Object.keys(event.record);
    const values = Object.values(event.record);

    const additionalInformation = [] as Array<number>;

    keys.forEach((key, index) => {
      if(Number([key][0])) {
        additionalInformation.push(Number([key][0]))
        additionalInformation.push(Number(values[index].value))
      }
    })

    const department = this.context.get('department') as number;
    const recordDate = this.context.get('recordDate') as moment.Moment;
    const formValues = event.record as FormValues;

    const body = {
      additionalInformation,
      apapHour: Number(formValues.recordTime.apapValue),
      department: Number(department),
      deviceId: formValues?.device?.deviceId,
      deviceSequence: formValues?.device?.sequenceNumber,
      observation: formValues.description ? formValues.description : '',
      occurrenceDate: recordDate,
      occurrenceQuantity: formValues.occurrences ? formValues.occurrences : 0,
      released: 'N',
      retroactiveJustification: formValues.retroactiveJustification ? formValues.retroactiveJustification : '',
      side: formValues?.side?.sideSequence,
      topographySequence: formValues?.topography?.sequenceNumber,
      typeSequence: formValues.type.sequenceNumber,
      volume: formValues.volume ? formValues.volume : 0,
      weight: formValues.weight ? formValues.weight : 0
    }

    HttpService.get<IntakesOutputsLimitResponse>(Endpoint.INTAKES_OUTPUTS_CONSIST_LIMIT, { patientId, sequenceNumber: body.typeSequence, volume: body.volume })
      .then(response => {
        const returnMessage = response.body?.ds_retorno_p;
        if(returnMessage) {
          return Promise.reject(returnMessage);
        }
        return Promise.resolve();
      })
      .then(() => this.handleFormSubmit(event, sequenceNumber as number, body))
      .catch(error => {
        ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
        this.consistValues(event);
      });

  }

  consistValues(event: AbortableSubmitEvent): void {
    event.abort();
    this.intakesOutputsformApi.setEditing(true);
    this.intakesOutputsformApi.setDisabled(false);
  }

  handleFormSubmit(event: AbortableSubmitEvent, sequenceNumber: string | number, body: Record<string, Value>): void {
    if(sequenceNumber) {
      this.updateIntakesOutputs(body, event);
      return;
    }
    this.insertIntakesOutputs(body);
  }
}

export default IntakesAndOutputsEditorController;

